import type { UserDto } from '@api/WhisApi/generated/model/userDto';
import type { SelectOption } from '@components/Select/select.types';
import { useAppTranslation } from '@hooks/useAppTranslation';
import { UserForm } from '@screens/UserManagement/components/UserForm/UserForm';
import { buildUserFormConfig } from '@screens/UserManagement/components/UserForm/userForm.config';
import type { UserFormValues } from '@screens/UserManagement/components/UserForm/userForm.types';
import { buildUserFormDefaults } from '@screens/UserManagement/components/UserForm/userForm.utils';
import React, { useMemo } from 'react';
import { Modal, Pressable, StyleSheet } from 'react-native';
import { H1, SizableText, useTheme, YStack } from 'tamagui';

type UserManagementMode = 'create' | 'edit';

interface UserManagementModalProps {
  open: boolean;
  mode: UserManagementMode;
  user?: UserDto | null;
  countryOptions: SelectOption[];
  isCountryLocked: boolean;
  lockedCountry?: UserDto['country'];
  onClose: () => void;
  onSave?: (values: UserFormValues) => void;
}

export const UserManagementModal = ({
  open,
  mode,
  user,
  countryOptions,
  isCountryLocked,
  lockedCountry,
  onClose,
  onSave,
}: UserManagementModalProps): React.ReactNode => {
  const { t, language } = useAppTranslation();
  const theme = useTheme();

  const defaultValues = useMemo(
    () => buildUserFormDefaults(user, lockedCountry),
    [lockedCountry, user],
  );

  const formConfig = useMemo(
    () =>
      buildUserFormConfig({
        t,
        language,
        countryOptions,
        isCountryLocked,
        lockedCountry,
      }),
    [countryOptions, isCountryLocked, language, lockedCountry, t],
  );

  const handleSave = (values: UserFormValues): void => {
    onSave?.(values);
    onClose();
  };

  return (
    <Modal
      visible={open}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <YStack
        flex={1}
        justifyContent="center"
        alignItems="center"
        padding="$true"
      >
        <Pressable
          style={[
            { ...StyleSheet.absoluteFillObject },
            { backgroundColor: theme.shadow?.val },
          ]}
          onPress={onClose}
        />
        <YStack
          bordered
          elevate
          borderRadius="$true"
          padding="$true"
          backgroundColor="$background"
          maxWidth="$compactPageWidth"
          width="100%"
          maxHeight="90%"
        >
          <H1>
            {mode === 'create'
              ? t('userManagement.modal.createTitle')
              : t('userManagement.modal.editTitle')}
          </H1>
          <SizableText>
            {mode === 'create'
              ? t('userManagement.modal.createDescription')
              : t('userManagement.modal.editDescription')}
          </SizableText>
          <UserForm
            config={formConfig}
            defaultValues={defaultValues}
            isOpen={open}
            onSubmit={handleSave}
            onCancel={onClose}
          />
        </YStack>
      </YStack>
    </Modal>
  );
};
